import { useQueryClient } from '@tanstack/react-query'
import { Clock, Send } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { useClientPortalFlows } from '@/features/client-portal/hooks/use-portal-admin'
import { useFlowsQuery } from '@/features/flows/hooks/use-flows'
import { queryKeys } from '@/shared/constants/query-keys'
import { EmailSuggestions } from '@/shared/form-sections/email-suggestions'
import { Button } from '@/shared/ui/button'
import { Field, FieldDescription, FieldError, FieldLabel } from '@/shared/ui/field'
import { Input } from '@/shared/ui/input'
import { NativeSelect, NativeSelectOption } from '@/shared/ui/native-select'

/** Presets de vigencia del enlace (PRD §9). */
const EXPIRATION_OPTIONS: { value: number; label: string }[] = [
  { value: 1, label: '24 horas' },
  { value: 2, label: '48 horas' },
  { value: 7, label: '7 días' },
  { value: 30, label: '30 días' }
]

/** Validación básica de forma del correo (el backend re-valida). */
const isValidEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())

type ClientCreateFlowFormProps = {
  clientId: string
  clientType: 'individual' | 'juridical'
  clientName: string
  clientEmail: string | null
}

/** "Crear flujo de validación": creates and sends the portal flow to the client. */
export function ClientCreateFlowForm({
  clientId,
  clientType,
  clientName,
  clientEmail
}: ClientCreateFlowFormProps) {
  const queryClient = useQueryClient()
  const { createAndSend } = useClientPortalFlows(clientId)
  // Sólo los flujos del tipo de cliente (+ los 'both').
  const { data: flows = [] } = useFlowsQuery('client_portal', clientType)
  // Cliente YA existente: sólo flujos del contexto de validación de cliente existente
  // (los de alta y los de revalidación se ofrecen en sus propios contextos).
  const activeFlows = flows.filter((f) => f.active && f.context === 'client_validation')
  const [flowDefinitionId, setFlowDefinitionId] = useState('')
  const [expirationDays, setExpirationDays] = useState(7)
  // El correo es del CLIENTE: se prellena con el que ya tenga y es editable. Al enviar, el
  // backend lo persiste en el cliente (se conserva aunque el flujo se cancele), así el
  // próximo envío ya lo trae. Esto destraba el caso "cliente sin correo".
  const [email, setEmail] = useState(clientEmail ?? '')
  const [emailTouched, setEmailTouched] = useState(false)
  const emailValid = isValidEmail(email)

  // Selecciona la primera definición activa; re-selecciona si la elegida ya no aplica.
  useEffect(() => {
    if (activeFlows.length === 0) return
    if (!activeFlows.some((f) => f.id === flowDefinitionId)) {
      setFlowDefinitionId(activeFlows[0].id)
    }
  }, [activeFlows, flowDefinitionId])

  const handleSend = () => {
    if (!emailValid) {
      setEmailTouched(true)
      toast.error('Ingresa un correo electrónico válido')
      return
    }
    if (!flowDefinitionId) {
      toast.error('Selecciona un tipo de validación')
      return
    }
    createAndSend.mutate(
      {
        clientId,
        clientType,
        clientEmail: email.trim(),
        clientName,
        flowDefinitionId,
        expirationDays
      },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: queryKeys.clients.all })
        }
      }
    )
  }

  return (
    <section className="flex flex-col gap-3">
      <Field data-invalid={emailTouched && !emailValid}>
        <FieldLabel htmlFor="clientEmail">Correo electrónico</FieldLabel>
        <Input
          id="clientEmail"
          type="email"
          inputMode="email"
          value={email}
          placeholder="cliente@correo.com"
          onChange={(e) => {
            setEmail(e.target.value)
          }}
          onBlur={() => {
            setEmailTouched(true)
          }}
          aria-invalid={(emailTouched && !emailValid) || undefined}
          aria-required
        />
        <EmailSuggestions value={email} onSelect={setEmail} />
        {emailTouched && !emailValid ? (
          <FieldError errors={[{ message: 'Ingresa un correo electrónico válido.' }]} />
        ) : (
          <FieldDescription>
            Se enviará el enlace a este correo y quedará guardado en el cliente.
          </FieldDescription>
        )}
      </Field>

      <Field>
        <FieldLabel htmlFor="flowDefinition">Tipo de validación</FieldLabel>
        <NativeSelect
          id="flowDefinition"
          value={flowDefinitionId}
          onChange={(e) => {
            setFlowDefinitionId(e.target.value)
          }}
        >
          {activeFlows.length === 0 ? (
            <NativeSelectOption value="" disabled>
              No hay flujos configurados
            </NativeSelectOption>
          ) : (
            activeFlows.map((flow) => (
              <NativeSelectOption key={flow.id} value={flow.id}>
                {flow.name}
              </NativeSelectOption>
            ))
          )}
        </NativeSelect>
      </Field>

      <Field>
        <FieldLabel htmlFor="expirationDays">
          <span className="flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" />
            Vigencia del enlace
          </span>
        </FieldLabel>
        <NativeSelect
          id="expirationDays"
          value={String(expirationDays)}
          onChange={(e) => {
            setExpirationDays(Number(e.target.value))
          }}
        >
          {EXPIRATION_OPTIONS.map((opt) => (
            <NativeSelectOption key={opt.value} value={String(opt.value)}>
              {opt.label}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      </Field>

      <Button
        type="button"
        className="w-full gap-2"
        onClick={handleSend}
        disabled={createAndSend.isPending || !emailValid || !flowDefinitionId}
      >
        <Send className="h-4 w-4" />
        {createAndSend.isPending ? 'Enviando…' : 'Crear y enviar'}
      </Button>
    </section>
  )
}
